import { getConfig } from "../config";
import type {
  AdminListResponse,
  CreateFeedItemRequest,
  CreateResponse,
  FeedStatsResponse,
  ListParams,
  PreviewFeedItemsResponse,
  UpdateFeedItemRequest,
} from "./types";

let accessTokenGetter: () => Promise<string> = async () => "";

export function setAccessTokenFn(fn: () => Promise<string>): void {
  accessTokenGetter = fn;
}

export function getAccessTokenFn(): () => Promise<string> {
  return accessTokenGetter;
}

function apiBase(): string {
  return getConfig().apiUrl.replace(/\/$/, "");
}

async function withAuth(
  init: RequestInit = {},
): Promise<RequestInit> {
  const token = await accessTokenGetter();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body != null) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return { ...init, headers };
}

async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const text = await res.text().catch(() => "");
  throw new Error(text || `${res.status} ${res.statusText}`);
}

export async function listFeedItems(
  params?: ListParams,
): Promise<AdminListResponse> {
  const url = new URL(`${apiBase()}/rss/feeds`, window.location.origin);
  if (params?.limit != null) {
    url.searchParams.set("limit", String(params.limit));
  }
  if (params?.marker != null) {
    url.searchParams.set("marker", String(params.marker));
  }
  if (params?.contentType) {
    url.searchParams.set("contentType", params.contentType);
  }
  if (params?.tag) {
    url.searchParams.set("tag", params.tag);
  }
  if (params?.staged !== undefined) {
    url.searchParams.set("staged", String(params.staged));
  }
  const res = await fetch(url.toString(), await withAuth());
  await throwIfNotOk(res);
  return res.json() as Promise<AdminListResponse>;
}

export async function fetchFeedStats(): Promise<FeedStatsResponse> {
  const res = await fetch(`${apiBase()}/rss/feeds/stats`, await withAuth());
  await throwIfNotOk(res);
  return res.json() as Promise<FeedStatsResponse>;
}

export async function previewFeedItems(): Promise<PreviewFeedItemsResponse> {
  const res = await fetch(`${apiBase()}/rss/feeds/preview`, await withAuth());
  await throwIfNotOk(res);
  return res.json() as Promise<PreviewFeedItemsResponse>;
}

export async function createFeedItem(
  body: CreateFeedItemRequest,
): Promise<CreateResponse> {
  const res = await fetch(`${apiBase()}/rss/feeds`, {
    ...(await withAuth({
      method: "POST",
      body: JSON.stringify(body),
    })),
  });
  await throwIfNotOk(res);
  return res.json() as Promise<CreateResponse>;
}

export async function updateFeedItem(
  id: string,
  data: UpdateFeedItemRequest,
): Promise<void> {
  const res = await fetch(`${apiBase()}/rss/feeds/${encodeURIComponent(id)}`, {
    ...(await withAuth({
      method: "PUT",
      body: JSON.stringify(data),
    })),
  });
  await throwIfNotOk(res);
}

export async function deleteFeedItem(id: string): Promise<void> {
  const res = await fetch(`${apiBase()}/rss/feeds/${encodeURIComponent(id)}`, {
    ...(await withAuth({ method: "DELETE" })),
  });
  await throwIfNotOk(res);
}

export async function pinFeedItem(id: string, pinned: boolean): Promise<void> {
  const res = await fetch(
    `${apiBase()}/rss/feeds/${encodeURIComponent(id)}/pin`,
    {
      ...(await withAuth({
        method: "PATCH",
        body: JSON.stringify({ pinned }),
      })),
    },
  );
  await throwIfNotOk(res);
}

export async function stageFeedItem(id: string, staged: boolean): Promise<void> {
  const res = await fetch(
    `${apiBase()}/rss/feeds/${encodeURIComponent(id)}/stage`,
    {
      ...(await withAuth({
        method: "PATCH",
        body: JSON.stringify({ staged }),
      })),
    },
  );
  await throwIfNotOk(res);
}
